/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { ShellScreenLoading } from '../../shared/layout/ShellScreenLoading'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { styles } from './ReportsScreen.styles';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader';
import { OperationalFilterChipScrollRow, OperationalFilterSegmentedRow } from '../../shared/filters/OperationalFilterPrimitives';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { ReportsAgendaView } from './ReportsAgendaView';
import { ReportsComprasView } from './ReportsComprasView';
import { ReportsOperationView } from './ReportsOperationView';
import { ReportsPersonalView } from './ReportsPersonalView';
import { ReportsResumenView } from './ReportsResumenView';
import { ReportsVentasView } from './ReportsVentasView';
import type { ReportsScreenViewModel } from './useReportsScreen';
import {
  REPORTS_SECTIONS,
  REPORTS_SECTION_LABELS,
  type ReportsSection,
} from './reportsScreen.constants';

const SECTION_SUBTITLES: Record<ReportsSection, string> = {
  resumen: 'Cómo viene el local en este período.',
  ventas: 'Cuánto vendiste, el margen y el detalle por ítem.',
  personal: 'Quién vendió y qué conviene mirar.',
  operacion: 'Caja, stock, cierres y sincronización.',
  compras: 'Compras del período por proveedor y producto.',
  agenda: 'Turnos completados por profesional.',
};

function sectionHasExport(section: ReportsSection): boolean {
  return section === 'resumen' || section === 'personal' || section === 'operacion' || section === 'ventas' || section === 'compras';
}

export function ReportsScreenView({ vm }: { vm: ReportsScreenViewModel }) {
  const { layoutWide } = useContentLayout();
  const containerStyle = layoutWide
    ? [styles.container, styles.containerWide]
    : [styles.container, styles.containerCompact];

  const sectionOptions = useMemo(
    () =>
      REPORTS_SECTIONS.filter((s) => vm.allowedSections.includes(s)).map((s) => ({
        value: s,
        label: REPORTS_SECTION_LABELS[s],
      })),
    [vm.allowedSections],
  );

  if (vm.branchesLoading && !vm.currentBranchId) {
    return (
      <View style={containerStyle}>
        <ShellScreenLoading label="Cargando sucursales…" />
      </View>
    );
  }

  if (!vm.currentBranchId) {
    return (
      <View style={containerStyle}>
        <View style={styles.reportsStateContent}>
          <ModuleEmptyState
            variant="nest"
            kicker="Informes"
            title="No hay una sucursal activa"
            subtitle="Elegí una sucursal para ver sus informes."
            primaryAction={{ label: 'Cambiar sucursal', onPress: vm.handleOpenBranchSelector }}
          />
        </View>
      </View>
    );
  }

  if (vm.loadError) {
    return (
      <View style={containerStyle}>
        <View style={styles.reportsStateContent}>
          <ModuleEmptyState
            variant="nest"
            kicker="Informes"
            title="No se pudieron cargar los informes"
            subtitle={vm.loadError}
            primaryAction={{ label: 'Reintentar', onPress: vm.onRefresh }}
            secondaryAction={{ label: 'Cambiar sucursal', onPress: vm.handleOpenBranchSelector }}
          />
        </View>
      </View>
    );
  }

  if (vm.loading && !vm.cashSummary && !vm.inventoryDashboard && !vm.periodSalesSnapshot) {
    return (
      <View style={containerStyle}>
        <ShellScreenLoading label="Cargando informes…" />
      </View>
    );
  }

  return (
    <ShellContentFrame style={containerStyle}>
      <ReportsRootHeader vm={vm} />
      <View style={headerStyles.toggleWrap}>
        {layoutWide ? (
          <OperationalFilterSegmentedRow
            options={sectionOptions}
            value={vm.activeSection}
            onChange={vm.setActiveSection}
            ticketTone
            testIDPrefix="informes.section"
          />
        ) : (
          <OperationalFilterChipScrollRow
            options={sectionOptions}
            value={vm.activeSection}
            onChange={vm.setActiveSection}
            ticketTone
            testIDPrefix="informes.section"
          />
        )}
      </View>
      <View style={styles.reportsContentScroll}>
        {vm.activeSection === 'resumen' ? (
          <ReportsResumenView vm={vm} />
        ) : vm.activeSection === 'ventas' ? (
          <ReportsVentasView vm={vm} />
        ) : vm.activeSection === 'personal' ? (
          <ReportsPersonalView vm={vm} />
        ) : vm.activeSection === 'compras' ? (
          <ReportsComprasView vm={vm} />
        ) : vm.activeSection === 'agenda' ? (
          <ReportsAgendaView vm={vm} />
        ) : (
          <ReportsOperationView vm={vm} />
        )}
      </View>
    </ShellContentFrame>
  );
}

function ReportsRootHeader({ vm }: { vm: ReportsScreenViewModel }) {
  const title = REPORTS_SECTION_LABELS[vm.activeSection];
  const subtitle = SECTION_SUBTITLES[vm.activeSection];

  const exportPress = vm.activeSection === 'personal'
    ? vm.handleSharePersonalRanking
    : vm.activeSection === 'ventas'
      ? vm.handleShareVentasSummary
      : vm.activeSection === 'compras'
        ? vm.handleShareCompras
        : vm.handleShareSummary;

  const trailing = sectionHasExport(vm.activeSection) && vm.canExport ? (
    <TouchableOpacity
      style={headerStyles.exportTile}
      onPress={exportPress}
      disabled={vm.loading}
      accessibilityRole="button"
      accessibilityLabel={vm.activeSection === 'compras' ? 'Exportar informe de compras' : 'Exportar resumen del período'}
      testID="informes.header.export"
    >
      <Ionicons name="download-outline" size={16} color={ticketColors.ink2} />
      <Text style={headerStyles.exportTileText}>Exportar</Text>
    </TouchableOpacity>
  ) : undefined;

  return (
    <TicketModuleHeader
      kicker="Informes"
      title={title}
      meta={subtitle}
      trailing={trailing}
      testID="informes.header"
    />
  );
}

const headerStyles = {
  toggleWrap: {
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  exportTile: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 6,
    height: 42,
    borderRadius: 10,
    paddingHorizontal: 14,
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  exportTileText: {
    fontSize: 13,
    fontWeight: '700' as const,
    color: ticketColors.ink,
  },
};
